import { useEffect, useState, useCallback } from "react";
import api from "../api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  TrendingUp, RefreshCw, Sliders, CalendarDays, Percent, BedDouble, Gauge,
  Sparkles, Loader2, Check, PartyPopper, CalendarPlus, Trash2,
} from "lucide-react";

const HORIZONS = [7, 30, 60, 90];
const FACTOR_COLOR = {
  season: "bg-teal-100 text-teal-700", weekend: "bg-indigo-100 text-indigo-700",
  holiday: "bg-rose-100 text-rose-700", event: "bg-fuchsia-100 text-fuchsia-700",
  demand_high: "bg-orange-100 text-orange-700", demand_low: "bg-sky-100 text-sky-700",
  last_minute: "bg-amber-100 text-amber-800", gap_night: "bg-purple-100 text-purple-700",
};
const eur = (n) => n == null ? "—" : `€${Math.round(n)}`;
const fmtDay = (iso) => new Date(iso + "T00:00:00").toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "short" });

const PROFILE_FIELDS = [
  { group: "Prezzi base", fields: [
    ["base_price", "Prezzo ideale (base)", "€"], ["min_price", "Prezzo minimo", "€"],
    ["max_price", "Prezzo massimo", "€"], ["min_acceptable_price", "Prezzo minimo accettabile", "€"],
    ["single_night_surcharge_pct", "Maggiorazione 1 notte", "%"],
  ]},
  { group: "Calendario & domanda", fields: [
    ["weekend_uplift_pct", "Incremento weekend", "%"], ["holiday_uplift_pct", "Incremento festività", "%"],
    ["event_uplift_pct", "Incremento eventi (default)", "%"],
    ["high_occupancy_threshold", "Soglia alta occupazione", "%"], ["high_occupancy_uplift_pct", "Incremento alta domanda", "%"],
    ["low_occupancy_threshold", "Soglia bassa occupazione", "%"], ["low_occupancy_discount_pct", "Sconto bassa occupazione", "%"],
    ["last_minute_days", "Last minute (giorni)", "gg"], ["last_minute_discount_pct", "Sconto last minute", "%"],
    ["gap_night_discount_pct", "Sconto notte orfana", "%"],
  ]},
  { group: "Soggiorno & obiettivi", fields: [
    ["long_stay_nights", "Soggiorno lungo (notti)", "n"], ["long_stay_discount_pct", "Sconto soggiorno lungo", "%"],
    ["min_stay", "Permanenza minima", "n"], ["ideal_stay", "Permanenza ideale", "n"],
    ["lead_time_target_days", "Lead time target", "gg"],
    ["min_desired_occupancy", "Occupazione minima desiderata", "%"], ["target_occupancy", "Occupazione target", "%"],
  ]},
];

function Kpi({ icon: Icon, label, value, sub, color }) {
  return (
    <Card className="p-4" data-testid={`revenue-kpi-${label}`}>
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-slate-400">
        <Icon size={14} style={{ color }} /> {label}
      </div>
      <div className="mt-1 text-2xl font-bold text-slate-800">{value}</div>
      {sub && <div className="text-xs text-slate-400">{sub}</div>}
    </Card>
  );
}

export default function Revenue() {
  const [apts, setApts] = useState([]);
  const [aptId, setAptId] = useState("");
  const [days, setDays] = useState(90);
  const [metrics, setMetrics] = useState(null);
  const [data, setData] = useState(null);
  const [profile, setProfile] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [priceEdits, setPriceEdits] = useState({});
  const [profileOpen, setProfileOpen] = useState(false);
  const [pdraft, setPdraft] = useState(null);
  const [newEvent, setNewEvent] = useState({ name: "", city: "Milano", start_date: "", end_date: "", uplift_pct: 20 });

  useEffect(() => {
    api.get("/apartments/lookup").then((r) => {
      setApts(r.data);
      const paolo = r.data.find((a) => /paolo sarpi 8/i.test(a.name));
      setAptId(paolo?.id || r.data[0]?.id || "");
    }).catch(() => toast.error("Errore caricamento immobili"));
  }, []);

  const load = useCallback(() => {
    if (!aptId) return;
    setLoading(true);
    Promise.all([
      api.get(`/revenue/metrics/${aptId}`),
      api.get(`/revenue/suggestions/${aptId}?days=${days}`),
      api.get(`/revenue/profile/${aptId}`),
      api.get(`/revenue/events`),
    ]).then(([m, s, p, e]) => {
      setMetrics(m.data); setData(s.data); setProfile(p.data); setEvents(e.data); setPriceEdits({});
    }).catch(() => toast.error("Errore caricamento Revenue")).finally(() => setLoading(false));
  }, [aptId, days]);
  useEffect(() => { load(); }, [load]);

  const aptName = apts.find((a) => a.id === aptId)?.name || "";

  const saveFullPrice = async (date) => {
    const v = priceEdits[date];
    if (v == null || v === "") return;
    await api.put(`/revenue/current-prices/${aptId}`, { prices: [{ date, full_price: Number(v) }], source: "manual" });
    toast.success("Full Price salvato");
    load();
  };

  const applySuggestion = async (row) => {
    try {
      await api.post(`/revenue/apply/${aptId}`, { date: row.date, suggested_price: row.suggested_price, applied_price: row.suggested_price, accepted: true });
      toast.success(`Prezzo ${eur(row.suggested_price)} applicato per ${fmtDay(row.date)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nell'applicazione del prezzo");
    }
  };

  const openProfile = () => { setPdraft({ ...profile }); setProfileOpen(true); };
  const saveProfile = async () => {
    const payload = {};
    PROFILE_FIELDS.forEach((g) => g.fields.forEach(([k]) => { payload[k] = Number(pdraft[k]); }));
    payload.seasonal_curves = pdraft.seasonal_curves;
    await api.put(`/revenue/profile/${aptId}`, payload);
    toast.success("Regole Revenue aggiornate");
    setProfileOpen(false); load();
  };

  const addEvent = async () => {
    if (!newEvent.name || !newEvent.start_date) return toast.error("Nome e data inizio obbligatori");
    await api.post(`/revenue/events`, { ...newEvent, end_date: newEvent.end_date || newEvent.start_date });
    toast.success("Evento aggiunto"); setNewEvent({ name: "", city: "Milano", start_date: "", end_date: "", uplift_pct: 20 }); load();
  };
  const delEvent = async (id) => { await api.delete(`/revenue/events/${id}`); load(); };

  const setSeason = (i, key, val) => {
    const c = [...(pdraft.seasonal_curves || [])]; c[i] = { ...c[i], [key]: key === "uplift_pct" ? Number(val) : val }; setPdraft({ ...pdraft, seasonal_curves: c });
  };

  return (
    <div className="space-y-6" data-testid="revenue-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-800">
            <TrendingUp className="text-emerald-600" /> Revenue Engine
          </h1>
          <p className="text-sm text-slate-500">Suggerimenti prezzo spiegabili · modalità <b>{data?.mode === "assisted" ? "Assistita" : data?.mode}</b> · nessun aggiornamento automatico</p>
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="revenue-apartment-select" value={aptId} onChange={(e) => setAptId(e.target.value)}
            className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm">
            {apts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <Button variant="outline" size="sm" onClick={openProfile} data-testid="revenue-rules-btn" className="gap-1.5"><Sliders size={15} /> Regole</Button>
          <Button variant="ghost" size="icon" onClick={load} data-testid="revenue-refresh-btn"><RefreshCw size={16} className={loading ? "animate-spin" : ""} /></Button>
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi icon={Gauge} label="ADR (30gg)" value={eur(metrics?.past_30d?.adr)} sub="Prezzo medio venduto" color="#059669" />
        <Kpi icon={Percent} label="RevPAR (30gg)" value={eur(metrics?.past_30d?.revpar)} sub="Ricavo per notte disponibile" color="#7C3AED" />
        <Kpi icon={BedDouble} label="Occupazione (30gg)" value={`${metrics?.past_30d?.occupancy_pct ?? 0}%`} sub="Passata" color="#0891B2" />
        <Kpi icon={CalendarDays} label={`Occ. futura (${days}gg)`} value={`${data?.future_occupancy_pct ?? 0}%`} sub="Prossimo periodo" color="#D97706" />
      </div>

      {/* Horizon filter */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-400">Orizzonte:</span>
        {HORIZONS.map((h) => (
          <button key={h} onClick={() => setDays(h)} data-testid={`revenue-horizon-${h}`}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${days === h ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
            {h} giorni
          </button>
        ))}
        <span className="ml-auto text-xs text-slate-400">Suggerito Urban OS vs Full Price (inserimento manuale)</span>
      </div>

      {/* Calendario prezzi */}
      <Card className="overflow-hidden">
        <div className="max-h-[540px] overflow-y-auto">
          <table className="w-full text-sm" data-testid="revenue-price-table">
            <thead className="sticky top-0 z-10 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400">
              <tr>
                <th className="p-3">Data</th>
                <th className="p-3">Suggerito Urban OS</th>
                <th className="p-3">Full Price</th>
                <th className="p-3">Differenza</th>
                <th className="p-3">Motivazione</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {(data?.suggestions || []).map((row) => (
                <tr key={row.date} data-testid={`revenue-row-${row.date}`}
                  className={`border-t border-slate-100 ${row.booked ? "bg-slate-50/70" : ""}`}>
                  <td className="p-3 whitespace-nowrap">
                    <span className={row.is_weekend ? "font-semibold text-indigo-700" : "text-slate-700"}>{fmtDay(row.date)}</span>
                    {row.is_holiday && <PartyPopper size={13} className="ml-1 inline text-rose-500" />}
                    {row.booked && <Badge className="ml-2 bg-slate-200 text-slate-500">Occupato</Badge>}
                  </td>
                  <td className="p-3 font-bold text-emerald-700" data-testid={`revenue-suggested-${row.date}`}>{eur(row.suggested_price)}</td>
                  <td className="p-3">
                    <Input type="number" data-testid={`revenue-fullprice-${row.date}`}
                      className="h-8 w-24" placeholder={row.full_price != null ? String(row.full_price) : "—"}
                      value={priceEdits[row.date] ?? (row.full_price ?? "")}
                      onChange={(e) => setPriceEdits({ ...priceEdits, [row.date]: e.target.value })}
                      onBlur={() => priceEdits[row.date] != null && saveFullPrice(row.date)} />
                  </td>
                  <td className="p-3 whitespace-nowrap">
                    {row.diff_eur == null ? <span className="text-slate-300">—</span> : (
                      <span className={row.diff_eur >= 0 ? "text-emerald-600" : "text-rose-600"}>
                        {row.diff_eur >= 0 ? "+" : ""}{eur(row.diff_eur)} ({row.diff_pct >= 0 ? "+" : ""}{row.diff_pct}%)
                      </span>
                    )}
                  </td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1">
                      {row.factors.length === 0 && <span className="text-xs text-slate-300">Prezzo base</span>}
                      {row.factors.map((f, i) => (
                        <span key={i} className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${FACTOR_COLOR[f.type] || "bg-slate-100 text-slate-600"}`}>{f.label}</span>
                      ))}
                    </div>
                  </td>
                  <td className="p-3">
                    <Button size="sm" variant="ghost" className="gap-1 text-emerald-700" data-testid={`revenue-apply-${row.date}`} onClick={() => applySuggestion(row)}>
                      <Check size={14} /> Applica
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Eventi cittadini */}
      <Card className="p-4" data-testid="revenue-events">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700"><Sparkles size={16} className="text-fuchsia-500" /> Eventi cittadini</div>
        <div className="mb-3 flex flex-wrap items-end gap-2">
          <div><label className="text-xs text-slate-400">Nome</label><Input className="h-9 w-44" data-testid="revenue-event-name" value={newEvent.name} onChange={(e) => setNewEvent({ ...newEvent, name: e.target.value })} placeholder="Salone del Mobile" /></div>
          <div><label className="text-xs text-slate-400">Città</label><Input className="h-9 w-32" value={newEvent.city} onChange={(e) => setNewEvent({ ...newEvent, city: e.target.value })} /></div>
          <div><label className="text-xs text-slate-400">Dal</label><Input type="date" className="h-9" data-testid="revenue-event-start" value={newEvent.start_date} onChange={(e) => setNewEvent({ ...newEvent, start_date: e.target.value })} /></div>
          <div><label className="text-xs text-slate-400">Al</label><Input type="date" className="h-9" value={newEvent.end_date} onChange={(e) => setNewEvent({ ...newEvent, end_date: e.target.value })} /></div>
          <div><label className="text-xs text-slate-400">Incremento %</label><Input type="number" className="h-9 w-24" value={newEvent.uplift_pct} onChange={(e) => setNewEvent({ ...newEvent, uplift_pct: Number(e.target.value) })} /></div>
          <Button size="sm" className="gap-1.5" data-testid="revenue-event-add" onClick={addEvent}><CalendarPlus size={15} /> Aggiungi</Button>
        </div>
        <div className="space-y-1">
          {events.length === 0 && <p className="text-xs text-slate-400">Nessun evento configurato.</p>}
          {events.map((e) => (
            <div key={e.id} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm">
              <span><b>{e.name}</b> · {e.city} · {e.start_date} → {e.end_date} · <span className="text-fuchsia-600">+{e.uplift_pct}%</span></span>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-500" onClick={() => delEvent(e.id)}><Trash2 size={14} /></Button>
            </div>
          ))}
        </div>
      </Card>

      {/* Dialog regole */}
      <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto" data-testid="revenue-rules-dialog">
          <DialogHeader><DialogTitle>Regole Revenue · {aptName}</DialogTitle></DialogHeader>
          {pdraft && (
            <div className="space-y-5">
              {PROFILE_FIELDS.map((g) => (
                <div key={g.group}>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">{g.group}</p>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {g.fields.map(([k, label, unit]) => (
                      <div key={k}>
                        <label className="text-xs text-slate-500">{label} <span className="text-slate-300">({unit})</span></label>
                        <Input type="number" data-testid={`revenue-field-${k}`} value={pdraft[k] ?? 0} onChange={(e) => setPdraft({ ...pdraft, [k]: e.target.value })} className="h-9" />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Curve stagionali</p>
                {(pdraft.seasonal_curves || []).map((s, i) => (
                  <div key={i} className="mb-2 flex flex-wrap items-end gap-2">
                    <div><label className="text-xs text-slate-400">Nome</label><Input className="h-9 w-40" value={s.name} onChange={(e) => setSeason(i, "name", e.target.value)} /></div>
                    <div><label className="text-xs text-slate-400">Dal (MM-DD)</label><Input className="h-9 w-24" value={s.start} onChange={(e) => setSeason(i, "start", e.target.value)} /></div>
                    <div><label className="text-xs text-slate-400">Al (MM-DD)</label><Input className="h-9 w-24" value={s.end} onChange={(e) => setSeason(i, "end", e.target.value)} /></div>
                    <div><label className="text-xs text-slate-400">%</label><Input type="number" className="h-9 w-20" value={s.uplift_pct} onChange={(e) => setSeason(i, "uplift_pct", e.target.value)} /></div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setProfileOpen(false)}>Annulla</Button>
            <Button onClick={saveProfile} data-testid="revenue-rules-save" className="bg-emerald-600 hover:bg-emerald-700">Salva regole</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
